import React, { useEffect, useMemo, useState } from 'react';
import { AlertTriangle, Check, Image as ImageIcon, Info, Play } from 'lucide-react';

import { extractHeadings, headingIds, inlineTokens, parseMd, safeImageSrc, wikiAssetId } from './wikiMarkdown';
import './wikiMarkdown.css';

/**
 * Read view of a Wiki page body. Renders the SAME blocks the editor edits
 * (`parseMd` from wikiMarkdown.js) with the prototype's block treatments
 * (`wkPgBlocks`: callouts, tables, checklists, figures, video cards, code)
 * and the CRM-core document typography (REBUILD-PLAN §1).
 *
 * Everything is rendered as React text — no HTML string is ever injected —
 * and links/images/embeds pass the engine's allow-lists.
 *
 * Props
 *   markdown      stored page Markdown
 *   blocks        optional pre-parsed blocks (skips parsing)
 *   anchors       give headings ids (default true; turn off for answers)
 *   idPrefix      heading id prefix (default 'wk-'), must match useWikiHeadings
 *   resolveAsset  optional (assetId) => url | Promise<url> for `/api/wiki/assets/<id>`
 *                 images (the asset route needs the auth header, so pass a
 *                 resolver that calls `GET /wiki/assets/{id}?format=json`)
 *   onNavigate    optional (href, event) => void for in-app relative links
 *                 (e.g. react-router navigate); default is a normal link
 *   className     extra class on the root
 */
export default function WikiMarkdownView({
    markdown = '',
    blocks: blocksProp = null,
    anchors = true,
    idPrefix = 'wk-',
    resolveAsset = null,
    onNavigate = null,
    className = '',
}) {
    const blocks = useMemo(() => blocksProp || parseMd(markdown), [blocksProp, markdown]);
    const ids = useMemo(() => (anchors ? headingIds(blocks, idPrefix) : new Map()), [anchors, blocks, idPrefix]);
    const groups = useMemo(() => groupLists(blocks), [blocks]);
    const ctx = { ids, resolveAsset, onNavigate };

    return (
        <div className={`wk-md${className ? ` ${className}` : ''}`} data-testid="wiki-markdown">
            {groups.map(group => (group.list
                ? (
                    <div key={`list-${group.start}`} role="list" className={`wk-md-list wk-md-list--${group.list}`}>
                        {group.items.map(({ block, index }) => <Block key={index} block={block} index={index} ctx={ctx} />)}
                    </div>
                )
                : <Block key={group.start} block={group.block} index={group.start} ctx={ctx} />))}
        </div>
    );
}

/** Heading list for the page's "On this page" rail (prototype: `#` and `##`). */
export function useWikiHeadings(markdown, { levels = [1, 2], idPrefix = 'wk-' } = {}) {
    const levelsKey = levels.join(',');
    return useMemo(
        () => extractHeadings(markdown || '', { levels: levelsKey.split(',').map(Number), idPrefix }),
        [markdown, levelsKey, idPrefix],
    );
}

export { extractHeadings };

const LIST_TYPES = { li: 'bullet', num: 'number', check: 'check' };

function groupLists(blocks) {
    const out = [];
    blocks.forEach((block, index) => {
        const list = LIST_TYPES[block.t];
        const prev = out[out.length - 1];
        if (list && prev && prev.list === list) prev.items.push({ block, index });
        else if (list) out.push({ list, start: index, items: [{ block, index }] });
        else out.push({ list: null, start: index, block });
    });
    return out;
}

function Block({ block: b, index, ctx }) {
    const id = ctx.ids.get(index);
    switch (b.t) {
    case 'h1':
        return <h2 id={id} className="wk-md-h1"><Inline text={b.text} ctx={ctx} /></h2>;
    case 'h2':
        return <h2 id={id} className="wk-md-h2"><Inline text={b.text} ctx={ctx} /></h2>;
    case 'h3':
        return <h3 id={id} className="wk-md-h3"><Inline text={b.text} ctx={ctx} /></h3>;
    case 'li':
        return (
            <div role="listitem" className="wk-md-row wk-md-li">
                <span className="wk-md-dot" aria-hidden="true" />
                <span className="wk-md-row-text"><Inline text={b.text} ctx={ctx} /></span>
            </div>
        );
    case 'num':
        return (
            <div role="listitem" className="wk-md-row wk-md-num">
                <span className="wk-md-num-mark">{`${b.n || 1}.`}</span>
                <span className="wk-md-row-text"><Inline text={b.text} ctx={ctx} /></span>
            </div>
        );
    case 'check':
        return (
            <div role="listitem" className="wk-md-row wk-md-check">
                <span
                    className={`wk-md-box${b.checked ? ' is-checked' : ''}`}
                    role="img"
                    aria-label={b.checked ? 'Done' : 'Not done'}
                >
                    {b.checked && <Check size={11} strokeWidth={3} aria-hidden="true" />}
                </span>
                <span className="wk-md-row-text"><Inline text={b.text} ctx={ctx} /></span>
            </div>
        );
    case 'callout': {
        const warn = b.tone === 'warn';
        const Icon = warn ? AlertTriangle : Info;
        return (
            <div role="note" className={`wk-md-callout wk-md-callout--${warn ? 'warn' : 'info'}`} data-marker={b.marker}>
                <Icon size={16} className="wk-md-callout-icon" aria-hidden="true" />
                <span><Inline text={b.text} ctx={ctx} /></span>
            </div>
        );
    }
    case 'quote':
        return <blockquote className="wk-md-quote"><Inline text={b.text} ctx={ctx} /></blockquote>;
    case 'divider':
        return <hr className="wk-md-hr" />;
    case 'code':
        return <pre className="wk-md-pre">{b.text}</pre>;
    case 'table':
        return <Table block={b} />;
    case 'image':
        return <Figure block={b} ctx={ctx} />;
    case 'embed':
        return (
            <a className="wk-md-embed" href={b.url} target="_blank" rel="noopener noreferrer">
                <Play size={28} aria-hidden="true" />
                <span className="wk-md-embed-cap">{`${b.provider} video · ${b.url}`}</span>
            </a>
        );
    default:
        return <p className="wk-md-p"><Inline text={b.text} ctx={ctx} /></p>;
    }
}

function Inline({ text, ctx }) {
    return inlineTokens(text).map((r, i) => {
        if (r.kind === 'b') return <strong key={i} className="wk-md-b">{r.text}</strong>;
        if (r.kind === 'i') return <em key={i} className="wk-md-i">{r.text}</em>;
        if (r.kind === 's') return <s key={i} className="wk-md-s">{r.text}</s>;
        if (r.kind === 'code') return <code key={i} className="wk-md-code">{r.text}</code>;
        if (r.kind === 'link') {
            // Unsafe scheme (javascript:, data:, ...): show the label, no link.
            if (!r.safeHref) return <React.Fragment key={i}>{r.text}</React.Fragment>;
            return <InlineLink key={i} href={r.safeHref} text={r.text} onNavigate={ctx.onNavigate} />;
        }
        return <React.Fragment key={i}>{r.text}</React.Fragment>;
    });
}

function InlineLink({ href, text, onNavigate }) {
    const external = /^(https?:|mailto:|\/\/)/i.test(href);
    if (external) {
        return <a className="wk-md-a" href={href} target="_blank" rel="noopener noreferrer">{text}</a>;
    }
    const onClick = onNavigate && href.startsWith('/')
        ? (event) => {
            if (event.defaultPrevented || event.button !== 0 || event.metaKey || event.ctrlKey || event.shiftKey || event.altKey) return;
            event.preventDefault();
            onNavigate(href, event);
        }
        : undefined;
    return <a className="wk-md-a" href={href} onClick={onClick}>{text}</a>;
}

function Table({ block }) {
    const head = block.head || [];
    const rows = block.rows || [];
    const cols = Math.max(1, head.length || (rows[0] || []).length || 1);
    const grid = { gridTemplateColumns: `repeat(${cols}, minmax(0, 1fr))` };
    return (
        <div className="wk-md-table" role="table" aria-colcount={cols}>
            <div className="wk-md-tr wk-md-thead" role="row" style={grid}>
                {head.map((h, i) => <div key={i} role="columnheader" className="wk-md-th">{h}</div>)}
            </div>
            {rows.map((r, ri) => (
                <div key={ri} className="wk-md-tr" role="row" style={grid}>
                    {r.map((c, ci) => <div key={ci} role="cell" className="wk-md-td">{c}</div>)}
                </div>
            ))}
        </div>
    );
}

function Figure({ block, ctx }) {
    const url = block.url || '';
    const usable = !!url && !/^https?:\/\/$/i.test(url) && !!safeImageSrc(url);
    return (
        <figure className="wk-md-figure">
            <div className="wk-md-frame">
                {usable
                    ? <WikiImage url={url} alt={block.text || ''} resolveAsset={ctx.resolveAsset} />
                    : (
                        <span className="wk-md-img-empty">
                            <ImageIcon size={16} aria-hidden="true" />
                            Add an image link, or upload one
                        </span>
                    )}
            </div>
            <figcaption className="wk-md-caption">{block.text || ''}</figcaption>
        </figure>
    );
}

function WikiImage({ url, alt, resolveAsset }) {
    const assetId = resolveAsset ? wikiAssetId(url) : null;
    const [resolved, setResolved] = useState(null);
    useEffect(() => {
        if (!assetId) return undefined;
        let live = true;
        setResolved(null);
        Promise.resolve()
            .then(() => resolveAsset(assetId))
            .then((u) => { if (live) setResolved(/^https?:\/\//i.test(String(u || '')) ? String(u) : null); })
            .catch(() => { if (live) setResolved(null); });
        return () => { live = false; };
    }, [assetId, resolveAsset]);
    const src = assetId ? resolved : url;
    if (!src) return null;
    return <img className="wk-md-img" src={src} alt={alt} loading="lazy" />;
}
